<template>
  <MainLoader v-if="flagLoading" />
  <div
    class="w-full h-screen relative bg-brand-gradient overflow-hidden"
    v-else
  >
    <div class="flex justify-center pt-4">
      <img
        src="/teopm.png"
        class="w-[200px] h-[105px] rounded-lg bg-white/95 p-2 shadow-lg"
      />
    </div>

    <div
      class="max-w-md mx-auto mt-6 p-8 bg-white rounded-xl shadow-lg border-t-4 border-accent-500"
    >
      <div class="text-center mb-6">
        <h1 class="text-2xl font-bold text-brand-900">Register User</h1>
      </div>

      <div class="space-y-4">
        <!-- First Name -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            First Name
          </label>
          <input
            v-model="firstName"
            type="text"
            placeholder="First Name"
            class="block w-full px-4 py-2 border rounded-md shadow-sm focus:ring-brand-500 focus:border-brand-500 sm:text-sm"
            :class="{
              'border-red-400': !firstName,
              'border-green-400': firstName,
            }"
          />
        </div>

        <!-- Last Name -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            Last Name
          </label>
          <input
            v-model="lastName"
            type="text"
            placeholder="Last Name"
            class="block w-full px-4 py-2 border rounded-md shadow-sm focus:ring-brand-500 focus:border-brand-500 sm:text-sm"
            :class="{
              'border-red-400': !lastName,
              'border-green-400': lastName,
            }"
          />
        </div>

        <!-- Email -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            Email
          </label>
          <input
            v-model="email"
            type="email"
            placeholder="mail@example.com"
            class="block w-full px-4 py-2 border rounded-md shadow-sm focus:ring-brand-500 focus:border-brand-500 sm:text-sm"
            :class="{
              'border-red-400': !isEmailValid(email),
              'border-green-400': isEmailValid(email),
            }"
          />
        </div>

        <!-- Company -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            Company
          </label>
          <input
            v-model="company"
            type="text"
            placeholder="Company"
            class="block w-full px-4 py-2 border rounded-md shadow-sm focus:ring-brand-500 focus:border-brand-500 sm:text-sm"
            :class="{
              'border-red-400': !company,
              'border-green-400': company,
            }"
          />
        </div>

        <!-- Password -->
        <div class="relative">
          <label class="block text-sm font-medium text-gray-700 mb-1">
            Password
          </label>
          <div class="relative">
            <input
              v-model="password"
              :type="showPassword ? 'text' : 'password'"
              placeholder="********"
              class="block w-full px-4 py-2 border rounded-md shadow-sm focus:ring-brand-500 focus:border-brand-500 sm:text-sm"
              :class="{
                'border-red-400': !isPasswordValid(password),
                'border-green-400': isPasswordValid(password),
              }"
            />
            <button
              type="button"
              @click="toggleShowPassword"
              class="absolute right-3 top-1/2 transform -translate-y-1/2 text-brand-600 hover:text-brand-700"
            >
              {{ showPassword ? "Hide" : "Show" }}
            </button>
          </div>

          <div class="text-xs mt-2 space-y-1">
            <p :class="{ 'text-green-600': isPasswordLongEnough }">
              • Minimum 6 characters
            </p>
            <p :class="{ 'text-green-600': hasUpperCaseLetter }">
              • At least one uppercase letter
            </p>
            <p :class="{ 'text-green-600': hasSpecialCharacter }">
              • At least one special character
            </p>
          </div>
        </div>

        <!-- Confirm Password -->
        <div class="relative">
          <label class="block text-sm font-medium text-gray-700 mb-1">
            Confirm Password
          </label>
          <div class="relative">
            <input
              v-model="confirmPassword"
              :type="showConfirmPassword ? 'text' : 'password'"
              placeholder="********"
              class="block w-full px-4 py-2 border rounded-md shadow-sm focus:ring-brand-500 focus:border-brand-500 sm:text-sm"
              :class="{
                'border-red-400':
                  !confirmPassword || confirmPassword !== password,
                'border-green-400': confirmPassword === password,
              }"
            />
            <button
              type="button"
              @click="toggleShowConfirmPassword"
              class="absolute right-3 top-1/2 transform -translate-y-1/2 text-brand-600 hover:text-brand-700"
            >
              {{ showConfirmPassword ? "Hide" : "Show" }}
            </button>
          </div>
        </div>

        <!-- Buttons -->
        <div class="flex justify-end space-x-3 pt-4">
          <button
            @click="handleCancel"
            class="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500"
          >
            Cancel
          </button>
          <button
            @click="handleRegister"
            :disabled="!isFormValid"
            class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500"
            :class="{
              'bg-gray-400 cursor-not-allowed': !isFormValid,
              'bg-accent-gradient hover:brightness-105': isFormValid,
            }"
          >
            Register
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { ref, computed } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import MainLoader from "./MainLoader.vue";

export default {
  components: { MainLoader },
  setup() {
    const store = useStore();
    const router = useRouter();

    const flagLoading = ref(false);
    const firstName = ref("");
    const lastName = ref("");
    const email = ref("");
    const company = ref("");
    const password = ref("");
    const confirmPassword = ref("");
    const showPassword = ref(false);
    const showConfirmPassword = ref(false);

    const isEmailValid = (email) => /\S+@\S+\.\S+/.test(email);

    const isPasswordLongEnough = computed(() => password.value.length >= 6);
    const hasUpperCaseLetter = computed(() => /[A-Z]/.test(password.value));
    const hasSpecialCharacter = computed(() =>
      /[!@#$%^&*(),.?":{}|<>]/.test(password.value)
    );

    const isPasswordValid = (password) => {
      const minLength = 6;
      const hasUpperCase = /[A-Z]/.test(password);
      const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
      return password.length >= minLength && hasUpperCase && hasSpecialChar;
    };

    const isFormValid = computed(() => {
      return (
        firstName.value &&
        lastName.value &&
        isEmailValid(email.value) &&
        company.value &&
        isPasswordValid(password.value) &&
        confirmPassword.value === password.value
      );
    });

    const toggleShowPassword = () => {
      showPassword.value = !showPassword.value;
    };

    const toggleShowConfirmPassword = () => {
      showConfirmPassword.value = !showConfirmPassword.value;
    };

    const handleRegister = () => {
      flagLoading.value = true;
      if (isFormValid.value) {
        store
          .dispatch("addUser", {
            first_name: firstName.value,
            last_name: lastName.value,
            email: email.value,
            company: company.value,
            password: password.value,
          })
          .then((newAccO) => {
            console.log("🚀 ~ handleRegister ~ newAccO:", newAccO);
            firstName.value = "";
            lastName.value = "";
            email.value = "";
            company.value = "";
            password.value = "";
            confirmPassword.value = "";

            if (newAccO.ok) {
              flagLoading.value = false;
              router.push({ name: "Login" });
            }
          })
          .catch((error) => {
            console.error("Registration error:", error);
            flagLoading.value = false;
          });
      }
    };

    const handleCancel = () => {
      firstName.value = "";
      lastName.value = "";
      email.value = "";
      company.value = "";
      password.value = "";
      confirmPassword.value = "";

      router.push({ name: "Login" });
    };

    return {
      isPasswordLongEnough,
      hasUpperCaseLetter,
      hasSpecialCharacter,
      flagLoading,
      firstName,
      lastName,
      email,
      company,
      password,
      confirmPassword,
      isEmailValid,
      isPasswordValid,
      isFormValid,
      toggleShowPassword,
      showPassword,
      toggleShowConfirmPassword,
      showConfirmPassword,
      handleRegister,
      handleCancel,
    };
  },
};
</script>

<style scoped></style>